import React, { useState, useContext, useEffect } from 'react'
import InputBase from '@mui/material/InputBase';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { DropzoneArea } from 'react-mui-dropzone'

import { OperatorContext } from '../../contexts/OperatorContext';
import ManageUsersForm from './ManageUsersForm'

import { uploadImages } from '../../helpers/uploadImages'
import { geocodeAddress, GEOCODE_FAILED_NOTICE } from '../../helpers/geocode'
import Loader from '../Loader'
import config from '../../config/config'

import './CMSForm.css'
window.Buffer = window.Buffer || require("buffer").Buffer;

const maxFormSection = 1

const stateNames = [
    'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
    'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas',
    'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota',
    'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey',
    'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon',
    'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah',
    'Vermont', 'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'
];

const organizationTypeOptions = [
    { value: 'operator', label: 'Operator' },
    { value: 'consultant', label: 'Consultant' }
]

const productAccessOptions = [
    { value: 'vao', label: 'VAO' },
    { value: 'clearcaid', label: 'ClearCaid' }
]

const productAccessLabels = productAccessOptions.reduce((acc, o) => {
    acc[o.value] = o.label
    return acc
}, {})

const defaultData = {
    name: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    phone: '',
    logo: '',
    active: false,
    pending_approval: true,
    stripe_customer_id: '',
    color_primary: '',
    color_secondary: '',
    organization_type: 'operator',
    product_access: [],
    custom_billing: false,
    vao_enabled: null
}

const getErrorMessage = (err, fallback) => {
    const errorData = err.response?.data
    return errorData?.errors?.length
        ? errorData.errors.join(' ')
        : errorData?.msg || errorData?.message || errorData?.error || (typeof errorData === 'string' ? errorData : err.message || fallback)
}

function calendarDate(value) {
    const match = String(value ?? '').match(/^(\d{4})-(\d{2})-(\d{2})/)
    return match ? `${match[1]}-${match[2]}-${match[3]}` : ''
}

function formatRenewalLabel(value) {
    const match = String(value ?? '').match(/^(\d{4})-(\d{2})-(\d{2})/)
    if (!match) return ''
    const [, year, month, day] = match
    return `${Number(month)}/${Number(day)}/${year}`
}

const buildingProfileFields = [
    ['name', 'Name'],
    ['address', 'Address'],
    ['city', 'City'],
    ['state', 'State'],
    ['zip', 'Zip'],
    ['phone', 'Phone'],
]

const CMSForm = ({ closeForm, initialData, searchString }) => {
    const {
        createOperator,
        editOperator,
        getAdminBuildings,
        createAdminBuilding,
        updateAdminBuilding,
        archiveAdminBuilding,
        deleteAdminBuilding,
        updateBuildingLicense,
    } = useContext(OperatorContext)
    const [currSectionPass, setCurrSectionPass] = useState(false)
    const [formSection, setFormSection] = useState(1)
    const [inputData, setInputData] = useState(null)
    const [subscriptions, setSubscriptions] = useState([])
    const [buildings, setBuildings] = useState([])
    const [orgCustomBilling, setOrgCustomBilling] = useState(false)
    const [licenseDrafts, setLicenseDrafts] = useState({})
    const [newBuildingName, setNewBuildingName] = useState('')
    const [buildingsLoading, setBuildingsLoading] = useState(false)
    const [buildingActionId, setBuildingActionId] = useState('')
    const [buildingConfirm, setBuildingConfirm] = useState(null)
    const [buildingError, setBuildingError] = useState('')
    const [buildingSuccess, setBuildingSuccess] = useState('')
    const [profileDraft, setProfileDraft] = useState(null)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [error, setError] = useState('')
    // Set after a successful save that could not be geocoded; holds the form open.
    const [notice, setNotice] = useState('')
    const [activeTab, setActiveTab] = useState('organization')
    const isExistingBuildingEligible = Boolean(
        initialData?._id && ['operator', 'consultant'].includes(initialData.organization_type)
    )
    // Same gate as Buildings: brand-new orgs and hospital/other types have no Users tab.
    const showUsersTab = isExistingBuildingEligible
    const showOperatorControls = initialData?.organization_type !== 'hospital'
        && ['operator', 'consultant'].includes(inputData?.organization_type)

    useEffect(() => {
        if (initialData && Object.keys(initialData).length > 0) {
            // Parse color JSON if it exists
            let parsedColor = { primary: '', secondary: '' }
            if (initialData.color) {
                try {
                    parsedColor = JSON.parse(initialData.color)
                } catch (e) {
                    console.log('Error parsing color JSON:', e)
                }
            }
            
            setInputData({  
                ...initialData,
                color_primary: parsedColor.primary || '',
                color_secondary: parsedColor.secondary || '',
                organization_type: initialData.organization_type || 'operator',
                product_access: Array.isArray(initialData.product_access) ? initialData.product_access : [],
                custom_billing: initialData.custom_billing === true,
                vao_enabled: typeof initialData.vao_enabled === 'boolean' ? initialData.vao_enabled : null
            })
        }
        else {
            setInputData(defaultData)
        }
    }, [initialData])

    useEffect(() => {
        updateSectionPass()
    }, [inputData])

    useEffect(() => {
        if (!isExistingBuildingEligible) {
            setBuildings([])
            setSubscriptions([])
            setOrgCustomBilling(false)
            setLicenseDrafts({})
            setProfileDraft(null)
            return
        }

        let ignore = false
        setProfileDraft(null)
        setBuildingsLoading(true)
        setBuildingError('')
        getAdminBuildings(initialData._id)
            .then(({ buildings: buildingList, subscriptions: subscriptionList, custom_billing }) => {
                if (ignore) return
                setBuildings(buildingList)
                setSubscriptions(subscriptionList)
                setOrgCustomBilling(custom_billing)
                setLicenseDrafts(createLicenseDrafts(buildingList, subscriptionList))
            })
            .catch(err => {
                if (!ignore) setBuildingError(getErrorMessage(err, 'Failed to load Buildings.'))
            })
            .finally(() => {
                if (!ignore) setBuildingsLoading(false)
            })

        return () => {
            ignore = true
        }
    }, [initialData?._id, isExistingBuildingEligible])

    function createLicenseDrafts(buildingList, subscriptionList) {
        return buildingList.reduce((drafts, building) => {
            const subscription = subscriptionList.find(
                item => String(item.building_id) === String(building._id) && item.occupies_base_slot === true
            )
            drafts[String(building._id)] = {
                access: subscription?.status === 'active' ? 'on' : 'off',
                renewal_date: calendarDate(subscription?.next_billing_date),
            }
            return drafts
        }, {})
    }

    async function reloadBuildingData() {
        const { buildings: buildingList, subscriptions: subscriptionList, custom_billing } = await getAdminBuildings(initialData._id)
        setBuildings(buildingList)
        setSubscriptions(subscriptionList)
        setOrgCustomBilling(custom_billing)
        setLicenseDrafts(createLicenseDrafts(buildingList, subscriptionList))
    }

    function updateLicenseDraft(buildingId, field, value) {
        setLicenseDrafts(current => ({
            ...current,
            [buildingId]: {
                ...current[buildingId],
                [field]: value,
            },
        }))
    }

    async function handleCreateBuilding() {
        const name = newBuildingName.trim()
        if (!name) {
            setBuildingError('Building name is required.')
            return
        }

        setBuildingActionId('create')
        setBuildingError('')
        setBuildingSuccess('')
        try {
            await createAdminBuilding(initialData._id, { name, status: 'pending' })
            await reloadBuildingData()
            setNewBuildingName('')
            setBuildingSuccess(orgCustomBilling
                ? 'Building created. Turn access on to activate it.'
                : 'Building created in pending state. The owner can pay from Billing to activate it.')
        } catch (err) {
            setBuildingError(getErrorMessage(err, 'Failed to create Building.'))
        } finally {
            setBuildingActionId('')
        }
    }

    async function handleArchiveBuilding(buildingId) {
        setBuildingActionId(`archive-${buildingId}`)
        setBuildingError('')
        setBuildingSuccess('')
        try {
            await archiveAdminBuilding(initialData._id, buildingId)
            await reloadBuildingData()
            setBuildingSuccess('Building archived.')
        } catch (err) {
            // Keep the current list intact when the server refuses the archive.
            setBuildingError(getErrorMessage(err, 'Failed to archive Building.'))
        } finally {
            setBuildingActionId('')
        }
    }

    async function handleDeleteBuilding(buildingId) {
        setBuildingActionId(`delete-${buildingId}`)
        setBuildingError('')
        setBuildingSuccess('')
        try {
            await deleteAdminBuilding(initialData._id, buildingId)
            if (profileDraft?.buildingId === buildingId) setProfileDraft(null)
            await reloadBuildingData()
            setBuildingSuccess('Building deleted.')
        } catch (err) {
            setBuildingError(getErrorMessage(err, 'Failed to delete Building.'))
        } finally {
            setBuildingActionId('')
        }
    }

    async function handleSaveLicense(buildingId) {
        const draft = licenseDrafts[buildingId]
        const building = buildings.find(item => String(item._id) === buildingId)
        setBuildingActionId(`license-${buildingId}`)
        setBuildingError('')
        setBuildingSuccess('')
        try {
            await updateBuildingLicense(initialData._id, buildingId, {
                access: building?.is_primary ? 'on' : draft.access,
                renewal_date: draft.renewal_date || null,
            })
            await reloadBuildingData()
            setBuildingSuccess('License updated.')
        } catch (err) {
            setBuildingError(getErrorMessage(err, 'Failed to update license.'))
        } finally {
            setBuildingActionId('')
        }
    }

    function openProfileEditor(building) {
        if (building.status === 'archived') return
        setProfileDraft({
            buildingId: String(building._id),
            name: building.name || '',
            address: building.address || '',
            city: building.city || '',
            state: building.state || '',
            zip: building.zip || '',
            phone: building.phone || '',
        })
        setBuildingError('')
        setBuildingSuccess('')
    }

    function updateProfileDraft(field, value) {
        setProfileDraft(current => (current ? { ...current, [field]: value } : current))
    }

    async function handleSaveProfile() {
        if (!profileDraft) return
        const name = profileDraft.name.trim()
        if (!name) {
            setBuildingError('Building name is required.')
            setBuildingSuccess('')
            return
        }

        const buildingId = profileDraft.buildingId
        setBuildingActionId(`profile-${buildingId}`)
        setBuildingError('')
        setBuildingSuccess('')
        try {
            await updateAdminBuilding(initialData._id, buildingId, {
                name,
                address: profileDraft.address.trim(),
                city: profileDraft.city.trim(),
                state: profileDraft.state.trim(),
                zip: profileDraft.zip.trim(),
                phone: profileDraft.phone.trim(),
            })
            await reloadBuildingData()
            setProfileDraft(null)
            setBuildingSuccess('Building updated.')
        } catch (err) {
            setBuildingError(getErrorMessage(err, 'Failed to update Building.'))
        } finally {
            setBuildingActionId('')
        }
    }

    function handleChange(e) {
        const { name, value } = e.target;

        setInputData({
            ...inputData,
            [name]: value,
        });

        updateSectionPass()
    }

    function handleCloseForm(e) {
        // mousedown (not click): a failed save unmounts the spinner under the cursor,
        // and the leftover click would otherwise hit this overlay and dismiss the form.
        if (isSubmitting) return
        if (e.target === e.currentTarget) closeForm()
    }

    async function handleClick() {
        if (updateSectionPass()) {
            if (formSection !== maxFormSection) {
                setFormSection(formSection + 1)
            }
            else {
                setIsSubmitting(true)
                setError('')
                try {
                    // null when the address can't be geocoded — never blocks the save.
                    let locationData = await geocodeAddress(inputData)
                    let url = inputData?.logo?.file && await uploadImages([inputData.logo])
                    // let emails = inputData.emails.map(e => { return { address: e } })
                    // console.log(url)

                    // Build color JSON string only if at least one color is provided
                    let colorJson = null
                    if (inputData.color_primary || inputData.color_secondary) {
                        colorJson = JSON.stringify({
                            primary: inputData.color_primary || '',
                            secondary: inputData.color_secondary || ''
                        })
                    }

                    let data = {
                        ...inputData,
                        // Omitted when geocoding failed, so an edit keeps the stored value.
                        // Operators don't carry a `location` point — only the formatted address.
                        ...(locationData ? { formatted_address: locationData.formatted_address } : {}),
                        logo: inputData.logo?.file ? url[0]?.url : inputData.logo ? inputData.logo : null,
                        color: colorJson,
                        // emails: emails
                    }

                    // Remove the temporary color fields before sending to API
                    delete data.color_primary
                    delete data.color_secondary
                    // Product access is derived by the server from organization type and VAO state.
                    delete data.product_access
                    // Server-managed fields loaded with the record; sending them back would overwrite newer values.
                    delete data.permissions
                    delete data.building_write_version
                    delete data.createdAt
                    delete data.updatedAt
                    delete data.__v
                    if (!showOperatorControls) {
                        delete data.custom_billing
                    }
                    // Only operator orgs get VAO; the server ignores the flag for other types.
                    if (inputData.organization_type !== 'operator') {
                        delete data.vao_enabled
                    }

                    if (initialData && Object.keys(initialData).length > 0) {
                        const updated = await editOperator(initialData._id, data, searchString)
                        if (!updated) setError('Failed to save operator.')
                        // Saved. Hold the form open only to report the missing coordinates.
                        else if (!locationData) setNotice(GEOCODE_FAILED_NOTICE)
                        else closeForm()
                    }
                    else {
                        const created = await createOperator(data, searchString)
                        if (!created) setError('Failed to save operator.')
                        else if (!locationData) setNotice(GEOCODE_FAILED_NOTICE)
                        else closeForm()
                    }
                } catch (err) {
                    setError(getErrorMessage(err, 'Failed to save operator.'))
                } finally {
                    setIsSubmitting(false)
                }
            }
        }
    }

    function updateSectionPass() {
        let pass = false

        if (!inputData) return false

        if (formSection === 1) {
            if (
                inputData.name !== '' &&
                inputData.address !== '' &&
                inputData.city !== '' &&
                inputData.state !== '' &&
                inputData.zip !== '' &&
                inputData.phone !== ''
            ) {
                pass = true
                setCurrSectionPass(true)
            } else {
                setCurrSectionPass(false)
            }
        }
        else {
            pass = true
        }

        return pass
    }

    // function addInput(e, newElem) {
    //     const { name } = e.target;
    //     setInputData({
    //         ...inputData,
    //         [name]: [...inputData[name], newElem],
    //     });
    // }

    // function handleURLChange(e, index) {
    //     let arr = [...inputData.emails]
    //     arr[index] = e.target.value
    //     setInputData({
    //         ...inputData,
    //         emails: arr,
    //     });
    // }

    function handleToggleChange(e) {
        const { name, checked } = e.target;

        setInputData({
            ...inputData,
            [name]: checked,
        });

    }

    function handleImgChange(e, index) {
        if (e[0]) {
            setInputData({
                ...inputData,
                logo: {
                    name: e[0].name, url: null, file: e[0]
                }
            });
        }
    }

    return inputData && Object.keys(inputData).length > 0 && (
        <div onMouseDown={handleCloseForm} className="modal-overlay">
            <div className="manage-facility-form">
                {isExistingBuildingEligible && (
                    <div className="org-section" style={{ borderTop: 'none', marginTop: 0, paddingTop: 0 }}>
                        <Tabs
                            value={activeTab}
                            onChange={(_e, value) => setActiveTab(value)}
                            variant="scrollable"
                            scrollButtons="auto"
                            allowScrollButtonsMobile
                        >
                            <Tab label="Organization" value="organization" />
                            <Tab label="Buildings" value="buildings" />
                            {showUsersTab && <Tab label="Users" value="users" />}
                        </Tabs>
                    </div>
                )}
                {activeTab === 'organization' && (
                    <>
                        <FormSection1
                            inputData={inputData}
                            handleChange={(e) => { handleChange(e) }}
                            handleToggleChange={(e) => { handleToggleChange(e) }}
                            handleImgChange={(e) => { handleImgChange(e) }}
                            showOperatorControls={showOperatorControls}
                            handleVaoEnabledChange={(value) => {
                                setInputData({
                                    ...inputData,
                                    vao_enabled: value === 'default' ? null : value === 'true'
                                })
                            }}
                        />
                        {error && <p style={{ textAlign: 'center', margin: '5px 0px', color: '#E23844' }}>{error}</p>}
                        {notice && <p style={{ textAlign: 'center', margin: '5px 0px', color: '#B26A00' }}>{notice}</p>}
                        <div style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignContent: 'center' }}>
                            {notice ?
                                <Button className='manger-facility-btn' onClick={() => closeForm()}>Close</Button>
                                :
                                <Button style={formSection !== 1 ? { marginLeft: 10 } : {} && currSectionPass ? {} : { backgroundColor: 'lightgray', cursor: 'default' }} className='manger-facility-btn' disabled={isSubmitting} onClick={() => handleClick()}> {formSection === maxFormSection ? (isSubmitting ? 'Submitting...' : 'Submit') : 'Next'}</Button>
                            }
                        </div>
                    </>
                )}
                {activeTab === 'buildings' && isExistingBuildingEligible && (
                    <div className="org-section" aria-labelledby="building-management-heading">
                        <h3 id="building-management-heading" className="org-section-label">Buildings</h3>
                        <p style={{ fontSize: '12px', color: '#666', margin: '6px 0 10px' }}>
                            {orgCustomBilling
                                ? 'Custom billing is on for this organization. Turn each Building\u2019s access on or off and set a renewal date. Stripe is not used.'
                                : 'Buildings are activated when the owner pays through Stripe Checkout.'}
                        </p>

                        <label htmlFor="new_building_name">New Building Name</label>
                        <InputBase
                            className="manger-facility-input"
                            value={newBuildingName}
                            onChange={(e) => setNewBuildingName(e.target.value)}
                            type="text"
                            name="new_building_name"
                            id="new_building_name"
                            placeholder="Building name"
                            inputProps={{ 'aria-label': 'New Building name' }}
                        />
                        <Button
                            className="manger-facility-btn"
                            disabled={Boolean(buildingActionId)}
                            onClick={handleCreateBuilding}
                            aria-label="Create Building"
                        >
                            {buildingActionId === 'create' ? 'Creating...' : 'Add Building'}
                        </Button>

                        {buildingsLoading && <p role="status" style={{ fontSize: '12px', color: '#666' }}>Loading Buildings...</p>}
                        {buildingError && <p role="alert" style={{ color: '#E23844', fontSize: '13px' }}>{buildingError}</p>}
                        {buildingSuccess && <p role="status" style={{ color: '#2E7D32', fontSize: '13px' }}>{buildingSuccess}</p>}

                        {!buildingsLoading && buildings.length === 0 && (
                            <p className="org-empty-message">No Buildings found.</p>
                        )}

                        <Dialog
                            open={buildingConfirm !== null}
                            onClose={() => setBuildingConfirm(null)}
                            aria-labelledby="building-confirm-title"
                            aria-describedby="building-confirm-description"
                        >
                            <DialogTitle id="building-confirm-title">
                                {buildingConfirm?.action === 'delete' ? 'Delete Building' : 'Archive Building'}
                            </DialogTitle>
                            <DialogContent>
                                <DialogContentText id="building-confirm-description">
                                    {buildingConfirm?.action === 'delete'
                                        ? `Are you sure you want to permanently delete ${buildingConfirm?.building.name}? This cannot be undone.`
                                        : `Are you sure you want to archive ${buildingConfirm?.building.name}?`}
                                </DialogContentText>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={() => setBuildingConfirm(null)}>Cancel</Button>
                                <Button
                                    onClick={() => {
                                        const { action, building } = buildingConfirm
                                        setBuildingConfirm(null)
                                        if (action === 'delete') handleDeleteBuilding(String(building._id))
                                        else handleArchiveBuilding(String(building._id))
                                    }}
                                    autoFocus
                                >
                                    {buildingConfirm?.action === 'delete' ? 'Delete' : 'Archive'}
                                </Button>
                            </DialogActions>
                        </Dialog>

                        {buildings.map(building => {
                            const buildingId = String(building._id)
                            const subscription = subscriptions.find(
                                item => String(item.building_id) === buildingId && item.occupies_base_slot === true
                            )
                            const licenseDraft = licenseDrafts[buildingId] || { access: 'off', renewal_date: '' }
                            const renewalLabel = subscription ? formatRenewalLabel(subscription.next_billing_date) : ''
                            const isArchived = building.status === 'archived'
                            const editingProfile = profileDraft?.buildingId === buildingId
                            const addressLine = [building.address, building.city, building.state, building.zip]
                                .filter(Boolean)
                                .join(', ')
                            const detailLine = [addressLine, building.phone].filter(Boolean).join(' · ')
                            const editButton = !isArchived && !editingProfile ? (
                                <Button
                                    className="manger-facility-btn"
                                    disabled={Boolean(buildingActionId)}
                                    onClick={() => openProfileEditor(building)}
                                    aria-label={`Edit ${building.name}`}
                                >
                                    Edit
                                </Button>
                            ) : null
                            const archiveButton = !building.is_primary ? (
                                <Button
                                    style={{ backgroundColor: '#E23844', color: 'white' }}
                                    disabled={Boolean(buildingActionId)}
                                    onClick={() => setBuildingConfirm({ action: 'archive', building })}
                                    aria-label={`Archive ${building.name}`}
                                >
                                    {buildingActionId === `archive-${buildingId}` ? 'Archiving...' : 'Archive'}
                                </Button>
                            ) : null
                            const deleteButton = !building.is_primary ? (
                                <Button
                                    style={{ backgroundColor: '#E23844', color: 'white' }}
                                    disabled={Boolean(buildingActionId)}
                                    onClick={() => setBuildingConfirm({ action: 'delete', building })}
                                    aria-label={`Delete ${building.name}`}
                                >
                                    {buildingActionId === `delete-${buildingId}` ? 'Deleting...' : 'Delete'}
                                </Button>
                            ) : null

                            return (
                                <div key={buildingId} className="org-row" style={{ marginTop: 10 }}>
                                    <div className="org-section-header">
                                        <strong>{building.name}</strong>
                                        <span style={{ fontSize: '12px', color: '#666' }}>
                                            {building.is_primary ? 'Primary · ' : ''}{building.status}
                                        </span>
                                    </div>
                                    {!editingProfile && detailLine && (
                                        <p style={{ fontSize: '12px', color: '#666', margin: '0 0 6px' }}>
                                            {detailLine}
                                        </p>
                                    )}
                                    {editingProfile && (
                                        <div style={{ marginTop: 6 }}>
                                            {buildingProfileFields.map(([key, label]) => (
                                                <div key={key}>
                                                    <label htmlFor={`building-${key}-${buildingId}`} style={{ display: 'block', fontSize: 12, color: '#666' }}>
                                                        {label}
                                                    </label>
                                                    <InputBase
                                                        className="manger-facility-input"
                                                        value={profileDraft[key]}
                                                        onChange={(e) => updateProfileDraft(key, e.target.value)}
                                                        type="text"
                                                        name={`building-${key}-${buildingId}`}
                                                        id={`building-${key}-${buildingId}`}
                                                        placeholder={label}
                                                        inputProps={{ 'aria-label': `${label} for ${building.name}` }}
                                                    />
                                                </div>
                                            ))}
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                                <Button
                                                    className="manger-facility-btn"
                                                    disabled={Boolean(buildingActionId)}
                                                    onClick={handleSaveProfile}
                                                    aria-label={`Save ${building.name}`}
                                                >
                                                    {buildingActionId === `profile-${buildingId}` ? 'Saving...' : 'Save'}
                                                </Button>
                                                <Button
                                                    disabled={buildingActionId === `profile-${buildingId}`}
                                                    onClick={() => setProfileDraft(null)}
                                                    aria-label={`Cancel editing ${building.name}`}
                                                >
                                                    Cancel
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                    {subscription && (
                                        <p style={{ fontSize: '12px', color: '#666', margin: '0 0 8px' }}>
                                            {subscription.billing_source === 'cms' ? 'License' : 'Stripe'} · {subscription.status}
                                            {renewalLabel ? ` · Renews ${renewalLabel}` : ''}
                                        </p>
                                    )}

                                    {!isArchived && orgCustomBilling && (
                                        <>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 6 }}>
                                                {building.is_primary ? (
                                                    <span style={{ fontSize: 12, color: '#666' }}>Access on</span>
                                                ) : (
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={licenseDraft.access === 'on'}
                                                                onChange={(e) => updateLicenseDraft(
                                                                    buildingId,
                                                                    'access',
                                                                    e.target.checked ? 'on' : 'off'
                                                                )}
                                                                inputProps={{ 'aria-label': `Access for ${building.name}` }}
                                                            />
                                                        }
                                                        label={licenseDraft.access === 'on' ? 'Access on' : 'Access off'}
                                                    />
                                                )}
                                                <div>
                                                    <label htmlFor={`renewal-date-${buildingId}`} style={{ display: 'block', fontSize: 12, color: '#666' }}>
                                                        Renewal date
                                                    </label>
                                                    <InputBase
                                                        className="manger-facility-input"
                                                        style={{ minWidth: 160 }}
                                                        type="date"
                                                        value={licenseDraft.renewal_date}
                                                        onChange={(e) => updateLicenseDraft(buildingId, 'renewal_date', e.target.value)}
                                                        id={`renewal-date-${buildingId}`}
                                                        inputProps={{ 'aria-label': `Renewal date for ${building.name}` }}
                                                    />
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                                                {editButton}
                                                <Button
                                                    className="manger-facility-btn"
                                                    disabled={Boolean(buildingActionId)}
                                                    onClick={() => handleSaveLicense(buildingId)}
                                                    aria-label={`Save license for ${building.name}`}
                                                >
                                                    {buildingActionId === `license-${buildingId}` ? 'Saving...' : 'Save License'}
                                                </Button>
                                                {archiveButton}
                                                {deleteButton}
                                            </div>
                                        </>
                                    )}

                                    {!isArchived && !orgCustomBilling && (editButton || archiveButton || deleteButton) && (
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
                                            {editButton}
                                            {archiveButton}
                                            {deleteButton}
                                        </div>
                                    )}

                                    {isArchived && deleteButton && (
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
                                            {deleteButton}
                                        </div>
                                    )}
                                </div>
                            )
                        })}
                    </div>
                )}
                {activeTab === 'users' && showUsersTab && (
                    <div className="org-section">
                        <label className="org-section-label">Users in this organization</label>
                        <ManageUsersForm
                            organization_id={initialData._id}
                            organization_type={initialData.organization_type}
                            closeForm={closeForm}
                            inline
                        />
                    </div>
                )}
            </div>
            {isSubmitting && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 9999,
                    flexDirection: 'column',
                    gap: '20px'
                }}>
                    <Loader loading={true} height={60} />
                    <div style={{ color: 'white', fontSize: '18px', fontWeight: 'bold' }}>
                        Submitting form...
                    </div>
                </div>
            )}
        </div>
    )
}

export default CMSForm



const FormSection1 = ({ inputData, handleChange, handleToggleChange, handleImgChange, showOperatorControls, handleVaoEnabledChange }) => {
    // Determine the logo display - could be a string (existing logo filename) or an object (new upload)
    const getLogoUrl = () => {
        if (!inputData.logo) return null
        // If it's a new file being uploaded (has file property)
        if (inputData.logo.file) {
            return URL.createObjectURL(inputData.logo.file)
        }
        // If it's an existing logo (string filename)
        if (typeof inputData.logo === 'string') {
            return `${config.apiUrl}/images/${inputData.logo}`
        }
        return null
    }
    
    const getLogoName = () => {
        if (!inputData.logo) return null
        if (typeof inputData.logo === 'string') return inputData.logo
        if (inputData.logo.name) return inputData.logo.name
        return null
    }
    
    const logoUrl = getLogoUrl()
    const logoName = getLogoName()
    
    return <>
        <h3 className="manage-facility-form-title">Create an Operator</h3>
        <label htmlFor="name">Name</label>
        <InputBase className='manger-facility-input' value={inputData.name} onChange={(e) => handleChange(e)} type="text" name="name" id="name" placeholder='Name' />

        <label htmlFor="organization_type">Organization Type</label>
        <FormControl fullWidth>
            <Select
                className="manger-facility-input"
                value={inputData.organization_type || 'operator'}
                name="organization_type"
                id="organization_type"
                onChange={(e) => handleChange(e)}
            >
                {(inputData.organization_type === 'hospital'
                    ? [...organizationTypeOptions, { value: 'hospital', label: 'Hospital' }]
                    : organizationTypeOptions
                ).map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </Select>
        </FormControl>

        <label>Product Access</label>
        <p className="manger-facility-input" style={{ display: 'flex', alignItems: 'center', color: '#666', backgroundColor: '#f5f5f5' }}>
            {Array.isArray(inputData.product_access) && inputData.product_access.length > 0
                ? inputData.product_access.map(v => productAccessLabels[v] || v).join(', ')
                : 'None'}
        </p>

        {showOperatorControls && (
            <>
                <label htmlFor="custom_billing">Custom Billing</label>
                <div style={{ marginBottom: 10 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                id="custom_billing"
                                onChange={(e) => handleToggleChange(e)}
                                checked={inputData.custom_billing === true}
                                name="custom_billing"
                                inputProps={{ 'aria-label': 'Custom billing' }}
                            />
                        }
                        label={inputData.custom_billing ? 'Enabled' : 'Disabled'}
                    />
                </div>

                {inputData.organization_type === 'operator' && (
                    <>
                        <label htmlFor="vao_enabled">VAO Access</label>
                        <FormControl fullWidth>
                            <Select
                                className="manger-facility-input"
                                value={inputData.vao_enabled === null ? 'default' : String(inputData.vao_enabled)}
                                name="vao_enabled"
                                id="vao_enabled"
                                onChange={(e) => handleVaoEnabledChange(e.target.value)}
                                inputProps={{ 'aria-label': 'VAO access setting' }}
                            >
                                <MenuItem value="default">Default (Enabled)</MenuItem>
                                <MenuItem value="true">Enabled</MenuItem>
                                <MenuItem value="false">Disabled</MenuItem>
                            </Select>
                        </FormControl>
                    </>
                )}
            </>
        )}

        <label htmlFor="address">Address</label>
        <InputBase className='manger-facility-input' value={inputData.address} onChange={(e) => handleChange(e)} type="text" name="address" id="address" placeholder='Address' />

        <label htmlFor="city">City</label>
        <InputBase className='manger-facility-input' value={inputData.city} onChange={(e) => handleChange(e)} type="text" name="city" id="city" placeholder='City' />

        <label htmlFor="state">State</label>
        <FormControl fullWidth>
            <Select
                className="manger-facility-input"
                value={inputData.state}
                name="state"
                id="state"
                onChange={(e) => handleChange(e)}
            >
                {stateNames.map(e => <MenuItem key={e} value={e}>{e}</MenuItem>)}
            </Select>
        </FormControl>

        <label htmlFor="zip">Zip</label>
        <InputBase className='manger-facility-input' value={inputData.zip} onChange={(e) => handleChange(e)} type="number" name="zip" id="zip" placeholder='Zip' />

        <label htmlFor="phone">Phone</label>
        <InputBase className='manger-facility-input' value={inputData.phone} onChange={(e) => handleChange(e)} type="number" name="phone" id="phone" placeholder='Phone' />

        <label htmlFor="logo">Logo</label>
        <div style={{ marginBottom: 20 }}>
            <DropzoneArea
                acceptedFiles={['image/jpeg', 'image/png']}
                showPreviews={false}
                showPreviewsInDropzone={false}
                maxFileSize={5000000}
                filesLimit={1}
                onChange={(e) => handleImgChange(e)}
            />
            {logoUrl && (
                <div style={{ marginTop: 10 }}>
                    <img 
                        src={logoUrl} 
                        alt="Operator Logo" 
                        style={{ maxWidth: '200px', maxHeight: '100px', objectFit: 'contain', border: '1px solid #ddd', borderRadius: '4px', padding: '5px' }}
                        onError={(e) => { e.target.style.display = 'none' }}
                    />
                </div>
            )}
            {logoName && <p style={{ marginTop: 5, fontSize: '12px', color: '#666' }}>{logoName}</p>}
        </div>

        <hr style={{ margin: '20px 0' }} />

        <label htmlFor="color_primary">Operator Colors</label>
        <p style={{ fontSize: '12px', color: '#666', marginBottom: '10px' }}>Enter hex color codes (e.g., #254b5d)</p>
        <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
            <div style={{ flex: 1 }}>
                <label htmlFor="color_primary" style={{ fontSize: '12px' }}>Primary Color</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <InputBase 
                        className='manger-facility-input' 
                        value={inputData.color_primary || ''} 
                        onChange={(e) => handleChange(e)} 
                        type="text" 
                        name="color_primary" 
                        id="color_primary" 
                        placeholder='#254b5d' 
                    />
                </div>
            </div>
            <div style={{ flex: 1 }}>
                <label htmlFor="color_secondary" style={{ fontSize: '12px' }}>Secondary Color</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <InputBase 
                        className='manger-facility-input' 
                        value={inputData.color_secondary || ''} 
                        onChange={(e) => handleChange(e)} 
                        type="text" 
                        name="color_secondary" 
                        id="color_secondary" 
                        placeholder='#59bac7' 
                    />
                </div>
            </div>
        </div>

        <hr style={{ margin: '20px 0' }} />

        <label htmlFor="stripe_customer_id">Stripe Customer ID</label>
        <p>Do not change this value unless you know what you are doing. This is used to identify the operator in Stripe.</p>
        <InputBase className='manger-facility-input' value={inputData.stripe_customer_id} onChange={(e) => handleChange(e)} type="text" name="stripe_customer_id" id="stripe_customer_id" placeholder='Stripe Customer ID' />

        <label htmlFor="pending_approval">Pending approval</label>
        <div style={{ marginBottom: 10 }}>
            <FormControlLabel control={<Switch onChange={(e) => handleToggleChange(e)} checked={inputData.pending_approval} name="pending_approval" />} label={inputData?.pending_approval?.toString()} />
        </div>
    </>
}


// const FormSection2 = ({ inputData, addInput, handleURLChange }) => {
//     return <>
//         <h3 className="manage-facility-form-title">Add Emails Addresses</h3>
//         <label htmlFor="3d-link">Add Emails</label>
//         <button name='emails' onClick={(e) => addInput(e, "")}>Add Email</button>
//         {inputData.emails.map((string, index) => {
//             return <>
//                 <InputBase value={string} key={index} onChange={(e) => handleURLChange(e, index)} className='manger-facility-input' />


//             </>
//         })}
//     </>
// }